import { lazyWithRetry } from "@outschool/ui-utils";
import React from "react";

import { useLearnerClassroomContext } from "../../providers/LearnerClassroomProvider";
import { isLearnerClassroomV2Layout } from "./layoutPredicates";
import LearnerClassroomPage from "./LearnerClassroomPage";

interface LessonsPageProps {
  lessonUid?: string;
}

const ClassroomAssignmentsPage = lazyWithRetry<Record<string, never>>(() =>
  import("./ClassroomAssignmentsPage").then(m => ({
    default: m.ClassroomAssignmentsPage,
  })),
);
const ClassroomAnnouncementsPage = lazyWithRetry<Record<string, never>>(() =>
  import("./ClassroomAnnouncementsPage").then(m => ({
    default: m.ClassroomAnnouncementsPage,
  })),
);
const ClassroomLessonsPage = lazyWithRetry<LessonsPageProps>(() =>
  import("./ClassroomLessonsPage").then(m => ({
    default: m.ClassroomLessonsPage,
  })),
);
const ClassroomLessonsPageV2 = lazyWithRetry<LessonsPageProps>(() =>
  import("./ClassroomLessonsPageV2").then(m => ({
    default: m.ClassroomLessonsPageV2,
  })),
);
const PrivateClassMessagesRoute = lazyWithRetry(
  () => import("./PrivateClassMessagesRoute"),
);
const RecordingPage = lazyWithRetry(() => import("./RecordingPage"));
const SectionJoinPage = lazyWithRetry(() => import("./SectionJoinPage"));
const SinglePostPage = lazyWithRetry(() => import("./SinglePostPage"));

export const tabValues = {
  Classroom: "classroom",
  Announcements: "announcements",
  Lessons: "lessons",
  Assignments: "assignments",
  PrivateClassMessages: "private-class-messages",
};

const LessonsPageByLayout = ({ lessonUid }: LessonsPageProps) => {
  const { activity, section, syllabus } = useLearnerClassroomContext();

  if (isLearnerClassroomV2Layout(section, syllabus, activity)) {
    return <ClassroomLessonsPageV2 lessonUid={lessonUid} />;
  }
  return <ClassroomLessonsPage lessonUid={lessonUid} />;
};

export const classroomRoutes: {
  [key: string]: (routeParams: { [key: string]: string }) => {
    value?: string;
    renderPage: () => JSX.Element;
  };
} = {
  "/": () => ({
    value: tabValues.Classroom,
    renderPage: () => <LearnerClassroomPage />,
  }),
  "/announcements": () => ({
    value: tabValues.Announcements,
    renderPage: () => <ClassroomAnnouncementsPage />,
  }),
  "/lessons": () => ({
    value: tabValues.Lessons,
    renderPage: () => <LessonsPageByLayout />,
  }),
  "/lessons/:lessonUid": ({ lessonUid }) => ({
    value: tabValues.Lessons,
    renderPage: () => <LessonsPageByLayout lessonUid={lessonUid} />,
  }),
  "/assignments": () => ({
    value: tabValues.Assignments,
    renderPage: () => <ClassroomAssignmentsPage />,
  }),
  "/join/:enrollmentUid": ({ enrollmentUid }) => ({
    renderPage: () => <SectionJoinPage enrollmentUid={enrollmentUid} />,
  }),
  "/private-class-messages": () => ({
    value: tabValues.PrivateClassMessages,
    renderPage: () => <PrivateClassMessagesRoute />,
  }),
  "/posts/:classPostUid": ({ classPostUid }) => ({
    renderPage: () => <SinglePostPage classPostUid={classPostUid} />,
  }),
  "/recording/:recordingUid": ({ recordingUid }) => ({
    renderPage: () => <RecordingPage recordingUid={recordingUid} />,
  }),
};
